import { useState, type ReactNode } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { AccountMenu } from "./account-menu.tsx";
import { CartPreview } from "./cart-preview.tsx";
import { Bell, ChevronRight, Search, ShoppingCart } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { api } from "../../api/client.ts";
import { isPricedPage } from "../layout.tsx";
import { PurchaseBanner } from "../purchase-banner.tsx";
import { useCart, useMe } from "../../api/queries.ts";
import { useQuery } from "@tanstack/react-query";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版外壳
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **网页版的导航在顶上，不在底下。**
 *    底部标签栏是手机的形态 —— 拇指够得着。1440 宽的屏幕上把四个图标钉在
 *    窗口最下沿，鼠标要专门跑一趟到屏幕底部，而视线一直在页面上方。
 *
 * ⚠️ 顶栏**常驻**（sticky）：网页上一页能滚很长，导航跟着走才不用滚回顶部。
 */

const NAV = [
  { to: "/", label: "首页" },
  { to: "/products", label: "全部商品" },
  { to: "/cases", label: "装修案例" },
] as const;

function TopBar() {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { data: me } = useMe();
  const { data: cart } = useCart(Boolean(me));
  const cartCount = cart?.groups.reduce((n, g) => n + g.lines.length, 0) ?? 0;

  const unread = useQuery({
    queryKey: ["messages", "unread"],
    queryFn: () => api.get<{ count: number }>("/me/messages/unread"),
    enabled: Boolean(me),
    staleTime: 30_000,
  });

  const [kw, setKw] = useState("");

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background">
      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-10 px-8">
        <Link to="/" className="shrink-0 text-[18px] font-medium tracking-tight whitespace-nowrap">
          装修建材<span className="text-brand-600">·一站购齐</span>
        </Link>

        <nav className="flex shrink-0 items-center gap-7">
          {NAV.map((n) => {
            const active = n.to === "/" ? path === "/" : path.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                search={{} as never}
                className={cn(
                  "relative py-5 text-[15px] transition-colors hover:text-foreground",
                  active ? "font-medium text-foreground" : "text-muted-foreground",
                )}
              >
                {n.label}
                {/* ⚠️ 下划线标当前页 —— 网页上导航是一直可见的，得让人知道在哪。 */}
                {active && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-brand-600" />
                )}
              </Link>
            );
          })}
        </nav>

        <form
          className="relative min-w-0 flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            navigate({ to: "/products", search: { keyword: kw.trim() || undefined } as never });
          }}
        >
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-[18px] -translate-y-1/2 text-muted-foreground" />
          <input
            className="h-10 w-full rounded-lg border border-border bg-secondary/50 pr-4 pl-11 text-[14px] outline-none transition-colors focus:border-brand-500 focus:bg-background"
            placeholder="搜瓷砖、地板、灯具…"
            value={kw}
            onChange={(e) => setKw(e.target.value)}
          />
        </form>

        <div className="flex shrink-0 items-center gap-1">
          {me && (
            <Link
              to="/messages"
              className="relative flex size-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              aria-label="消息"
            >
              <Bell className="size-[19px]" />
              {(unread.data?.count ?? 0) > 0 && (
                <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-destructive" />
              )}
            </Link>
          )}

          {/*
            🔴 **滑过购物车掀开预览**（老板指定，参考 twshangcheng 的 `CartPreview`）：
               车里有什么、合计多少、两个出口，不用先跳去购物车页看一眼再回来。

            ⚠️ 跟身份卡同样那三条坑（见 `account-menu.tsx`）：
               用 CSS `group-hover` 不用 React state（顶栏里有受控的搜索框）；
               浮层挂在**外层 div** 上、不能放进 `<Link>` 里（否则卡里的按钮
               会同时触发外层那条去 `/cart` 的跳转）；
               图标和浮层之间**用 `pt-2` 撑开、不能用 `mt-2`** —— 留了真空隙，
               鼠标往下移的途中就离开 hover 区，卡片当场收掉，根本点不到。
            ⚠️ 只在有鼠标的设备上出（`hidden lg:block`）：触屏没有 hover，
               点一下图标直接进购物车页才对。
          */}
          <div className="group/cart relative">
            <Link
              to="/cart"
              className="relative flex h-10 items-center gap-2 rounded-lg px-3 text-[14px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <ShoppingCart className="size-[19px]" />
              购物车
              {cartCount > 0 && (
                <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-medium text-white tabular-nums">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </Link>

            <div className="pointer-events-none absolute top-full right-0 z-50 hidden pt-2 opacity-0 transition-opacity duration-150 group-hover/cart:pointer-events-auto group-hover/cart:opacity-100 lg:block">
              <CartPreview cart={cart} />
            </div>
          </div>

          {/*
            🔴 **滑过名字掀开身份卡**（切身份 + 退出登录）——
               老板指定，参考 twshangcheng 的会员卡浮窗。
               "我现在看到的是哪个价"是逛商城时随时要确认、随时要换的事，
               让他为了换视角先跳去账户页等于每次绕一圈。
            ⚠️ 登录/未登录两态都在这个组件里，外面不用再判一次。
          */}
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}

/** 网页版页面外壳。 */
export function DesktopShell({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="min-h-dvh bg-muted/30">
      <TopBar />
      {/*
        🔴 **换了价格体系必须说出来，网页版也一样。**
           手机版早就有这条提示，网页版漏掉的话，切到商家身份的人整站看到的是
           进货价却毫无说明 —— 他会以为平台降价了，或者拿这个数去跟客户报价。
        ⚠️ 只在"价格随身份变"的页面出（商品/购物车/结算/店铺/首页），
           商家后台页本来就不是零售价，到处贴一条只会变成噪音。
      */}
      {isPricedPage(path) && <PurchaseBanner wide />}
      <main className="mx-auto max-w-[1280px] px-8 py-8">{children}</main>
    </div>
  );
}

/**
 * 面包屑。
 *
 * 🔴 **网页版要有面包屑，手机版靠返回键。**
 *    鼠标操作没有"系统返回"，用户点进三层之后只能靠浏览器后退 ——
 *    而他常常想直接跳回中间那一层。
 */
export function Crumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="mb-5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
      {items.map((it, i) => (
        <span key={it.label} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="size-3.5 opacity-50" />}
          {it.to ? (
            <Link to={it.to} search={{} as never} className="transition-colors hover:text-foreground">
              {it.label}
            </Link>
          ) : (
            <span className="text-foreground">{it.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/**
 * 左导航 + 右内容的两栏版面（账户中心 / 商家端）。
 *
 * 🔴 **网页版这两处必须是固定的左侧导航。**
 *    手机上是一行行的列表项，点进去、再返回；网页上有的是横向空间，
 *    导航常驻在左边，切换时右边换内容 —— 不用来回跳。
 */
export function SideLayout({
  nav,
  children,
}: {
  nav: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[220px_minmax(0,1fr)] items-start gap-8">
      {/* ⚠️ `sticky` —— 右边内容可能很长，导航要一直在视野里。 */}
      <aside className="sticky top-24 rounded-xl border border-border bg-card p-2">{nav}</aside>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function SideNavItem({
  to,
  search,
  icon: Icon,
  label,
  hint,
  active,
  onClick,
}: {
  to?: string;
  search?: Record<string, unknown>;
  icon: LucideIcon;
  label: string;
  hint?: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <Icon className="size-[18px] shrink-0" />
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      {hint && <span className="shrink-0 text-[12px] text-muted-foreground">{hint}</span>}
    </>
  );
  const cls = cn(
    "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[14px] transition-colors",
    active
      ? "bg-brand-50 font-medium text-brand-700"
      : "text-muted-foreground hover:bg-secondary hover:text-foreground",
  );

  if (onClick || !to) {
    return (
      <button className={cls} onClick={onClick}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={to} search={(search ?? {}) as never} className={cls}>
      {inner}
    </Link>
  );
}

/** 分组小标题（左导航里用）。 */
export function SideNavGroup({ children }: { children: ReactNode }) {
  return (
    <p className="px-3 pt-3 pb-1.5 text-[12px] font-medium text-muted-foreground/70">{children}</p>
  );
}
